import type { Condition, Leaf, Op, TraceEntry } from "./types.ts";
import { getPath } from "./templates.ts";

const DAY_MS = 86_400_000;
const isLeaf = (c: unknown): c is Leaf =>
  !!c && typeof c === "object" && "field" in c && "op" in c;
const isGroup = (
  c: unknown,
): c is { all: Condition[] } | { any: Condition[] } =>
  !!c &&
  typeof c === "object" &&
  (Array.isArray((c as { all?: unknown }).all) ||
    Array.isArray((c as { any?: unknown }).any));

function resolveValue(value: unknown, ctx: object): unknown {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "ref" in value
  ) {
    return getPath(ctx, String((value as { ref: unknown }).ref));
  }
  return value;
}

function compare(
  op: Op,
  actual: unknown,
  expected: unknown,
  now: Date,
): boolean {
  switch (op) {
    case "eq":
      return actual !== undefined && actual === expected;
    case "neq":
      return actual !== expected;
    case "in":
      return Array.isArray(expected) && expected.includes(actual);
    case "nin":
      return !Array.isArray(expected) || !expected.includes(actual);
    case "gt":
      return (
        typeof actual === "number" &&
        typeof expected === "number" &&
        actual > expected
      );
    case "gte":
      return (
        typeof actual === "number" &&
        typeof expected === "number" &&
        actual >= expected
      );
    case "lt":
      return (
        typeof actual === "number" &&
        typeof expected === "number" &&
        actual < expected
      );
    case "lte":
      return (
        typeof actual === "number" &&
        typeof expected === "number" &&
        actual <= expected
      );
    case "exists":
      return actual !== undefined && actual !== null && actual !== "";
    case "not_exists":
      return actual === undefined || actual === null || actual === "";
    case "contains":
      if (typeof actual === "string")
        return typeof expected === "string" && actual.includes(expected);
      return Array.isArray(actual) && actual.includes(expected);
    case "days_since_gte": {
      if (typeof actual !== "string" || typeof expected !== "number")
        return false;
      const t = Date.parse(actual);
      return Number.isFinite(t) && (now.getTime() - t) / DAY_MS >= expected;
    }
    case "matches": {
      if (
        typeof actual !== "string" ||
        typeof expected !== "string" ||
        expected.length > 200
      )
        return false;
      try {
        // Cap the subject so a pathological pattern cannot run unbounded.
        return new RegExp(expected).test(actual.slice(0, 2000));
      } catch {
        return false;
      }
    }
    default:
      return false;
  }
}

const MAX_DEPTH = 3;

function evalNode(
  c: unknown,
  ctx: object,
  now: Date,
  trace: TraceEntry[],
  depth = 0,
): boolean {
  if (depth > MAX_DEPTH) {
    trace.push({
      field: "<max_depth>",
      op: "eq",
      expected: MAX_DEPTH,
      actual: depth,
      result: false,
    });
    return false;
  }
  if (isLeaf(c)) {
    const actual = getPath(ctx, c.field);
    const expected = resolveValue(c.value, ctx);
    const result = compare(c.op, actual, expected, now);
    trace.push({ field: c.field, op: c.op, expected, actual, result });
    return result;
  }
  if (isGroup(c)) {
    // Evaluate every child (no short-circuit) so the dry-run trace is complete.
    if ("all" in c && Array.isArray(c.all))
      return c.all
        .map((x) => evalNode(x, ctx, now, trace, depth + 1))
        .every(Boolean);
    return (c as { any: Condition[] }).any
      .map((x) => evalNode(x, ctx, now, trace, depth + 1))
      .some(Boolean);
  }
  return true; // {} and trigger-specific objects ({entity,days,stage_in}, {cron}) are pre-filtered by the scanner
}

export function evaluateConditions(
  c: unknown,
  ctx: object,
  now: Date,
): { matched: boolean; trace: TraceEntry[] } {
  const trace: TraceEntry[] = [];
  const matched = evalNode(c, ctx, now, trace);
  return { matched, trace };
}
